import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, imgUrl, formatApiError } from "../../lib/api";
import { ListHeader, RowActions, useCrudList } from "./AdminMagazines";
import { Field, inputCls, UploadField } from "../../components/admin/fields";
import { toast } from "sonner";

export function AdminPublishers() {
  const { items, remove } = useCrudList("publishers");
  return (
    <div data-testid="admin-publishers-page">
      <ListHeader title="Publishers" newTo="/apadmin/publishers/new" testId="new-publisher-btn" />
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="publishers-table">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-3">Logo</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Founded</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 last:border-0" data-testid={`publisher-row-${p.slug}`}>
                <td className="px-4 py-2">{p.logo && <img src={imgUrl(p.logo)} alt="" className="w-9 h-9 object-cover rounded-sm" />}</td>
                <td className="px-4 py-3 font-medium text-zinc-900">{p.name}</td>
                <td className="px-4 py-3 text-zinc-600">{p.founded_year || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{p.status}</span></td>
                <td className="px-4 py-3"><RowActions editTo={`/apadmin/publishers/${p.id}/edit`} onDelete={() => remove(p.id)} testId={`publisher-${p.slug}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY = { name: "", slug: "", logo: "", about: "", founded_year: "", website: "", email: "", address: "", status: "active" };

export function PublisherForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (id) adminApi.get(`/publishers/${id}`).then(setForm).catch(() => {}); }, [id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || undefined, founded_year: form.founded_year ? parseInt(form.founded_year) : null };
      if (id) await adminApi.put(`/publishers/${id}`, payload);
      else await adminApi.post("/publishers", payload);
      toast.success("Publisher saved.");
      navigate("/apadmin/publishers");
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl" data-testid="publisher-form">
      <h1 className="text-xl font-bold text-zinc-900 mb-6">{id ? "Edit Publisher" : "New Publisher"}</h1>
      <div className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name *"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="pub-name-input" /></Field>
        <Field label="Slug (auto if empty)"><input value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inputCls} data-testid="pub-slug-input" /></Field>
        <Field label="Founded year"><input type="number" value={form.founded_year || ""} onChange={(e) => set("founded_year", e.target.value)} className={inputCls} data-testid="pub-founded-input" /></Field>
        <Field label="Website"><input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} data-testid="pub-website-input" /></Field>
        <Field label="Contact email"><input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="pub-email-input" /></Field>
        <Field label="Status">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="pub-status-select">
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </Field>
        <div className="sm:col-span-2"><Field label="Address"><input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="pub-address-input" /></Field></div>
        <div className="sm:col-span-2"><Field label="About"><textarea rows={3} value={form.about} onChange={(e) => set("about", e.target.value)} className={inputCls} data-testid="pub-about-input" /></Field></div>
        <UploadField label="Logo" value={form.logo} onChange={(v) => set("logo", v)} accept="image/*" testId="pub-logo-upload" />
      </div>
      <div className="flex gap-3 mt-6">
        <button type="submit" disabled={saving} data-testid="pub-save-btn" className="bg-zinc-900 text-white text-sm font-semibold px-6 py-2.5 rounded-md hover:bg-zinc-700 disabled:opacity-60">
          {saving ? "Saving..." : "Save Publisher"}
        </button>
        <button type="button" onClick={() => navigate("/apadmin/publishers")} className="text-sm font-semibold px-6 py-2.5 rounded-md border border-zinc-300 hover:border-zinc-900">Cancel</button>
      </div>
    </form>
  );
}
